import { useMemo, useState } from "react";
import { Loader2, RefreshCcw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import type {
  OrderDetailResponse,
  RefundOrderRequest,
  RefundReason,
} from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api-client";
import { useRefundOrderMutation } from "@/features/orders/api";
import { REFUND_REASONS } from "@/lib/enum-labels";
import { useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

interface RefundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: OrderDetailResponse;
}

/**
 * İade modal'ı.
 *
 * Akış:
 * 1. Aktif (Status="Valid") biletler default olarak hepsi seçili gelir.
 * 2. Organizer bilet seçimini değiştirebilir; "Tümünü Seç/Kaldır" toggle.
 * 3. Reason zorunlu (Other'da reasonNote da zorunlu).
 * 4. Mail/SMS toggles default açık; SMS ancak buyerPhone varsa görünür.
 * 5. Tutar canlı: seçili biletlerin (per-unit base + tax + fee) toplamı.
 *    IsFree biletler payı 0 olarak gelir; UI'da "Bedava" badge'i ile gösterilir.
 *
 * Submit success → modal kapanır + toast.success.
 * Submit error → modal açık kalır, üstte error banner.
 */
export function RefundDialog({ open, onOpenChange, order }: RefundDialogProps) {
  const refundMut = useRefundOrderMutation(order.orderNumber);
  const t = useT("refundDialog");
  const tReason = useT("refundReason");
  const fmt = useFormat();
  const money = (value: number) => fmt.money(value, order.currency);

  const itemMap = useMemo(
    () => new Map(order.items.map((it) => [it.id, it])),
    [order.items],
  );

  // Aktif biletler — refund target'ı bunlar.
  const activeTickets = useMemo(
    () => order.tickets.filter((ticket) => ticket.status === "Valid"),
    [order.tickets],
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(activeTickets.map((ticket) => ticket.id)),
  );
  const [reason, setReason] = useState<RefundReason>("CustomerRequest");
  const [reasonNote, setReasonNote] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Modal her açıldığında state'i sıfırla — eski bir submit kalıntısı kalmasın.
  const [resetFor, setResetFor] = useState({ open, activeTickets });
  if (resetFor.open !== open || resetFor.activeTickets !== activeTickets) {
    setResetFor({ open, activeTickets });
    if (open) {
      setSelectedIds(new Set(activeTickets.map((ticket) => ticket.id)));
      setReason("CustomerRequest");
      setReasonNote("");
      setSendEmail(true);
      setSendSms(true);
      setErrorMessage(null);
    }
  }

  const refundAmount = useMemo(() => {
    let sum = 0;
    for (const ticket of activeTickets) {
      if (!selectedIds.has(ticket.id)) continue;
      const item = itemMap.get(ticket.orderItemId);
      if (!item || item.isFree) continue;
      const perUnit =
        item.quantity > 0 ? item.subtotal / item.quantity : 0;
      sum += perUnit + item.taxAmount + item.serviceFee;
    }
    return Math.round(sum * 100) / 100;
  }, [activeTickets, selectedIds, itemMap]);

  const allSelected =
    activeTickets.length > 0 && selectedIds.size === activeTickets.length;
  const isPartial =
    selectedIds.size > 0 && selectedIds.size < activeTickets.length;

  function toggleAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(activeTickets.map((ticket) => ticket.id)));
    }
  }

  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSubmit() {
    setErrorMessage(null);
    if (selectedIds.size === 0) {
      setErrorMessage(t("noneSelected"));
      return;
    }
    if (reason === "Other" && reasonNote.trim().length === 0) {
      setErrorMessage(t("noteRequired"));
      return;
    }

    const body: RefundOrderRequest = {
      reason,
      reasonNote: reasonNote.trim() ? reasonNote.trim() : null,
      ticketIds: allSelected ? null : Array.from(selectedIds),
      sendEmail,
      sendSms: sendSms && !!order.buyerPhone,
    };

    try {
      await refundMut.mutateAsync(body);
      toast.success(
        isPartial
          ? t("successPartial", { count: selectedIds.size })
          : t("successFull"),
      );
      onOpenChange(false);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.detail || t("failed")
          : e instanceof Error
            ? e.message
            : t("failed");
      setErrorMessage(msg);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      disableBackdropClose={refundMut.isPending}
    >
      <DialogContent>
        <DialogHeader
          title={t("title")}
          description={`${order.orderNumber} — ${order.eventTitle}`}
        />

        {errorMessage && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        <div className="space-y-5">
          {/* ----- Bilet seçimi ----- */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-medium">{t("ticketsHeading")}</h3>
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {allSelected ? t("deselectAll") : t("selectAll")}
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto rounded-md border">
              {activeTickets.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground">
                  {t("noActive")}
                </div>
              ) : (
                <ul className="divide-y">
                  {activeTickets.map((ticket) => {
                    const item = itemMap.get(ticket.orderItemId);
                    const perUnitGross = item
                      ? item.isFree
                        ? 0
                        : (item.quantity > 0
                            ? item.subtotal / item.quantity
                            : 0) +
                          item.taxAmount +
                          item.serviceFee
                      : 0;
                    const checked = selectedIds.has(ticket.id);
                    return (
                      <li
                        key={ticket.id}
                        className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted/40"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOne(ticket.id)}
                          className="h-4 w-4 cursor-pointer rounded border-input"
                          id={`ticket-${ticket.id}`}
                        />
                        <label
                          htmlFor={`ticket-${ticket.id}`}
                          className="flex flex-1 cursor-pointer items-center justify-between gap-2"
                        >
                          <div>
                            <div className="font-mono text-xs">
                              {ticket.ticketCode}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {ticket.ticketTypeName}
                              {item?.isFree && (
                                <span className="ms-2 rounded bg-muted px-1.5 py-0.5 text-[10px]">
                                  {t("free")}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="tabular-nums text-xs text-muted-foreground">
                            {money(perUnitGross)}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {isPartial && (
              <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-500">
                {t("partialNote", { count: activeTickets.length - selectedIds.size })}
              </p>
            )}
          </section>

          {/* ----- Reason ----- */}
          <section className="space-y-2">
            <label htmlFor="refund-reason" className="block text-sm font-medium">
              {t("reason")}
            </label>
            <select
              id="refund-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value as RefundReason)}
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            >
              {REFUND_REASONS.map((r) => (
                <option key={r} value={r}>
                  {tReason(r)}
                </option>
              ))}
            </select>
            <Textarea
              value={reasonNote}
              onChange={(e) => setReasonNote(e.target.value)}
              rows={2}
              maxLength={1000}
              aria-label={
                reason === "Other" ? t("notePlaceholderRequired") : t("notePlaceholderOptional")
              }
              placeholder={
                reason === "Other" ? t("notePlaceholderRequired") : t("notePlaceholderOptional")
              }
            />
          </section>

          {/* ----- Notif toggles ----- */}
          <section className="space-y-2">
            <h3 className="block text-sm font-medium">{t("notifyHeading")}</h3>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <span>{t("sendEmail", { email: order.buyerEmail })}</span>
            </label>
            <label
              className={`flex items-center gap-2 text-sm ${
                order.buyerPhone ? "cursor-pointer" : "opacity-60"
              }`}
              title={!order.buyerPhone ? t("noPhone") : undefined}
            >
              <input
                type="checkbox"
                checked={sendSms && !!order.buyerPhone}
                onChange={(e) => setSendSms(e.target.checked)}
                disabled={!order.buyerPhone}
                className="h-4 w-4 rounded border-input"
              />
              <span>
                {order.buyerPhone
                  ? t("sendSms", { phone: order.buyerPhone })
                  : t("sendSmsNoPhone")}
              </span>
            </label>
          </section>

          {/* ----- Tutar preview ----- */}
          <section className="rounded-md bg-muted/40 p-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">{t("amount")}</span>
              <span className="tabular-nums text-base font-semibold">
                {money(refundAmount)}
              </span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{t("amountHint")}</p>
          </section>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={refundMut.isPending}
          >
            {t("dismiss")}
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={refundMut.isPending || selectedIds.size === 0}
          >
            {refundMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4" />
            )}
            {isPartial ? t("submitPartial") : t("submitFull")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
